import React, { memo, ReactElement } from 'react'
import { useSelector } from 'react-redux'
import some from 'lodash/some'
import moment from 'moment'
import { FormattedDateTime } from 'transfix-ui/components/FormattedDateTime'
import { createStyles, makeStyles } from 'transfix-ui/core'
import Typography from 'transfix-ui/core/Typography'
import { selectCarriersList, dispatchersListSelector } from 'apps/fleet-planner/utils/selectors'
import { AllowedTimezones } from 'constants/AllowedTimezones'

const getStepContentColor = (isError: boolean, isLabel: boolean): string => {
  if (isError) return '#bd1a1d'
  if (isLabel) return '#697684'
  return '#0D0E10'
}

const useStepContentStyles = makeStyles(theme => {
  return createStyles<'root', { isError: boolean; isLabel: boolean }>({
    root: {
      overflow: 'hidden',
      fontFamily: theme.typography.fontFamily,
      color: props => getStepContentColor(props.isError, props.isLabel),
      fontSize: theme.typography.fontSize,
      lineHeight: 1.36,
      fontWeight: props => (props.isLabel ? 600 : 'normal'),
    },
  })
})

interface IContentProps {
  values: Record<string, any>
  label: string
  error: Record<string, any>
  keyName?: string
}

const LoadIdStepContent = memo<IContentProps>(({ error, label, values }) => {
  const { externalId } = values || {}
  const classes = useStepContentStyles({ isError: some(error, String), isLabel: !externalId })
  return (
    <Typography variant='body2' className={classes.root}>
      {externalId || label}
    </Typography>
  )
})

const DispatcherIdStepContent = memo<IContentProps>(({ error, label, values }) => {
  const dispatchers = useSelector(dispatchersListSelector)
  const { dispatcherId } = values || {}
  const { fullName } = dispatchers.find(d => d.id === dispatcherId) || {}
  const classes = useStepContentStyles({ isError: some(error, String), isLabel: !fullName })
  return (
    <Typography variant='body2' className={classes.root}>
      {fullName || label}
    </Typography>
  )
})

const DriverStepContent = memo<IContentProps>(({ error, values }) => {
  const { drivers } = values || {}
  const [driver1, driver2] = drivers || []
  const driver2Name = driver2?.name ? `, ${driver2?.name}` : ''
  const driverDisplayValue = driver1?.id ? `${driver1?.name}${driver2Name}` : 'Driver Unassigned'
  const classes = useStepContentStyles({ isError: some(error, String), isLabel: !driver1?.id })
  return (
    <Typography variant='body2' className={classes.root}>
      {driverDisplayValue}
    </Typography>
  )
})

const CarrierStepContent = memo<IContentProps>(({ error, label, values }) => {
  const carriers = useSelector(selectCarriersList)
  const { motorCarrier } = values || {}
  const { name } = motorCarrier || {}
  const displayValue = carriers.length > 1 ? name || label : ''
  const classes = useStepContentStyles({ isError: some(error, String), isLabel: !name })

  return (
    <Typography variant='body2' className={classes.root}>
      {displayValue}
    </Typography>
  )
})

const LocationStepContent = memo<IContentProps>(({ error, label, values }) => {
  const { addressLine1, locality, name, postalCode, subdivisionCode } = values || {}
  const hasRequiredAddressValues = addressLine1 && postalCode && subdivisionCode && locality && name
  const classes = useStepContentStyles({
    isError: some(error, String),
    isLabel: !hasRequiredAddressValues,
  })
  return (
    <Typography variant='body2' className={classes.root}>
      {hasRequiredAddressValues ? (
        <>
          <div>{addressLine1}</div>
          <div>
            {locality}, {subdivisionCode} {postalCode}
          </div>
        </>
      ) : (
        label
      )}
    </Typography>
  )
})

// TODO: Update this after new Appointment component is completed.
const TimeStepContent = memo<IContentProps>(({ error, label, values }) => {
  const { startAt, endAt, timezone = moment.tz.guess() } = values || {}
  const displayTimezone = timezone as AllowedTimezones
  const classes = useStepContentStyles({ isError: some(error, String), isLabel: !startAt })
  return (
    <Typography variant='body2' className={classes.root}>
      {startAt ? (
        <>
          <FormattedDateTime
            datetime={startAt}
            displayTimezone={displayTimezone}
            format='timeWeekdayDateAndYear'
            timeShort
            abbreviated
          />
          {endAt && (
            <>
              <span>{' - '}</span>
              <FormattedDateTime
                datetime={endAt}
                displayTimezone={displayTimezone}
                format='time'
                timeShort
              />
            </>
          )}
        </>
      ) : (
        label
      )}
    </Typography>
  )
})

const Content = memo<IContentProps>(
  ({ error, keyName, label, values }): ReactElement => {
    switch (keyName) {
      case 'dispatcher':
        return (
          <DispatcherIdStepContent error={error} values={values} label={label} keyName={keyName} />
        )
      case 'loadId':
        return <LoadIdStepContent error={error} values={values} label={label} keyName={keyName} />
      case 'driver':
        return <DriverStepContent error={error} values={values} label={label} keyName={keyName} />
      case 'carrier':
        return <CarrierStepContent error={error} values={values} label={label} keyName={keyName} />
      case 'location':
        return <LocationStepContent error={error} values={values} label={label} keyName={keyName} />
      case 'time':
        return <TimeStepContent error={error} values={values} label={label} keyName={keyName} />
      default:
        return <></>
    }
  }
)

interface IStepperContentProps {
  value: Record<string, any>
}

export const SidebarStepContent = memo<IStepperContentProps>(({ value }) => {
  const { error, keyName, label, value: contentValue } = value || {}
  return <Content error={error} values={contentValue} keyName={keyName} label={label} />
})
